import authedAxios from "@api/authedAxios";
import { tsRestClient } from "@api/tsRest";

import { ITechnology } from "@interfaces/portfolio.type";

export const getPortfolios = async () => {
  const { body, status } = await tsRestClient.portfolio.findAllTechnologiesByUserId({});
  if (status !== 200) {
    throw new Error("Failed to get technologies");
  }
  return body;
};

export const getTechnology = async (technologyId: string) => {
  const { body, status } = await tsRestClient.portfolio.findTechnology({
    params: { technologyId },
  });
  if (status === 403) {
    throw new Error("You do not have permission to view this technology");
  }
  if (status !== 200) {
    throw new Error("Failed to get technology");
  }
  return { body, status };
};

export const archiveTechnology = async (technologyId: string) => {
  const { body, status } = await tsRestClient.portfolio.archiveTechnology({
    body: null,
    params: { technologyId },
  });
  if (status !== 200) {
    throw new Error("Failed to delete technology");
  }
  return body;
};

export const createTechnologyFromPortfolio = async (name: string, description?: string) => {
  const { body, status } = await tsRestClient.portfolio.createTechnology({
    body: { description, name },
  });
  if (status !== 200) {
    throw new Error("Failed to create new technology");
  }
  return body;
};

export const createTechnologyFromCompanyIds = async (
  companyIds: string[],
  name: string,
  description?: string,
  searchId?: string,
) => {
  const requestBody: {
    companyIds: string[];
    description?: string;
    name: string;
    searchId?: string;
  } = { companyIds, description, name, searchId };

  const { body, status } = await tsRestClient.portfolio.createTechnologyFromCompanyIds({
    body: requestBody,
  });

  if (status !== 200) {
    throw new Error("Failed to create new technology from company ids");
  }

  return body;
};

export const addCompaniesToTechnology = async (technologyId: string, companyIds: string[], searchId?: string) => {
  // Define the request body with required properties
  const requestBody: {
    companyIds: string[];
    searchId?: string;
  } = { companyIds, searchId };

  const { body, status } = await tsRestClient.portfolio.addCompaniesToTechnology({
    body: requestBody,
    params: { technologyId },
  });

  if (status !== 200) {
    throw new Error("Failed to add companies to technology");
  }

  return body;
};

export const createTechnologyFromContactIds = async (
  contactIds: string[],
  name: string,
  description?: string,
  searchId?: string,
) => {
  const { body, status } = await tsRestClient.portfolio.createTechnologyFromContactIds({
    body: { contactIds, description, name, searchId },
  });
  if (status !== 200) {
    throw new Error("Failed to get create new technology");
  }
  return body;
};

export const addContactsToTechnology = async (technologyId: string, contactIds: string[], searchId?: string) => {
  const { body, status } = await tsRestClient.portfolio.addContactsToTechnology({
    body: { contactIds, searchId },
    params: { technologyId },
  });

  if (status !== 200) {
    throw new Error("Failed to add contacts to technology");
  }

  return body;
};

export const removeCompaniesFromTechnology = async (companyIds: string[], technologyId: string) => {
  const { body, status } = await tsRestClient.portfolio.removeCompaniesFromTechnology({
    body: { companyIds },
    params: { technologyId },
  });

  if (status !== 200) {
    throw new Error("Failed to remove companies from technology");
  }

  return body;
};

export const removeContactsFromTechnology = async (contactIds: string[], technologyId: string) => {
  const { body, status } = await tsRestClient.portfolio.removeContactsFromTechnology({
    body: { contactIds },
    params: { technologyId },
  });

  if (status !== 200) {
    throw new Error("Failed to remove contacts from technology");
  }

  return body;
};

export const getContactEmails = async (peopleIds: string[]) => {
  const { body, status } = await tsRestClient.contacts.getContactEmails({
    body: {
      peopleIds,
    },
  });

  if (status !== 200) {
    throw new Error("Failed to get contact emails");
  }

  return body;
};

export const updateTechnology = async (technologyId: string, name: string, description?: string) => {
  const { body, status } = await tsRestClient.portfolio.updateTechnology({
    body: { description, name },
    params: { technologyId },
  });

  if (status !== 200) {
    throw new Error("Failed to update technology");
  }

  return body;
};

export const getInsight = async (technologyId: string, companyId: string) => {
  const data = await authedAxios("v1").get(`portfolio/get-insight/${technologyId}/${companyId}`);

  return data.data as ITechnology;
};
